import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import { ResellerAuthorizationCard } from './ResellerAuthorizationCard'
import { useStyles } from './ResellerAuthorizationList.styles'
import { ButtonV2 } from '@labrav/react-components'
import { useSalesforceContext } from '@labrav/common'
import { type ResaleAuthorizationsResellerAgreements } from '../../../admin/modules/resaleAuthorizations/reducer'
import { CloudMarketplace } from '@labrav/react-components/lib/@types/types/forms'
import { setCloudMarketplaceProductListing } from '../../../modules/productListingFilter/action'
import { useDispatch } from '../../../store'

export interface ResellerAuthorizationListProps {
  resellerAuthorizations: ResaleAuthorizationsResellerAgreements[]
  loading?: boolean
  error?: string | null
  navigateToUrl?: (url: string) => void
  partnerId?: string
}

export const ResellerAuthorizationList: React.FC<
  ResellerAuthorizationListProps
> = ({
  resellerAuthorizations,
  loading = false,
  error = null,
  navigateToUrl,
  partnerId,
}) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const initialCollapsedSet = useMemo(() => {
    return new Set(
      resellerAuthorizations.map(
        (resellerAuthorization, index) =>
          `reseller-authorization-${index}-${resellerAuthorization.id}`
      )
    )
  }, [resellerAuthorizations])

  const [collapsedResellerAuthorizations, setCollapsedResellerAuthorizations] =
    useState<Set<string>>(initialCollapsedSet)

  useEffect(() => {
    const newIds = resellerAuthorizations.map(
      (resellerAuthorization, index) =>
        `reseller-authorization-${index}-${resellerAuthorization.id}`
    )
    setCollapsedResellerAuthorizations(prev => {
      const updated = new Set(prev)
      newIds.forEach(id => {
        if (!updated.has(id)) {
          updated.add(id)
        }
      })
      return updated
    })
  }, [resellerAuthorizations])

  const { actions } = useSalesforceContext()

  const toggleResellerAuthorizationCollapse = useCallback(
    (resellerAuthorizationId: string) => {
      setCollapsedResellerAuthorizations(prev => {
        const next = new Set(prev)
        if (next.has(resellerAuthorizationId)) {
          next.delete(resellerAuthorizationId)
        } else {
          next.add(resellerAuthorizationId)
        }
        return next
      })
    },
    []
  )

  const handleResellerAuthorizationClick = useCallback(
    async (
      resellerAuthorizationId: string,
      productId: string,
      draft: boolean,
      cloudAccountId?: string,
      cloudMarketplace?: CloudMarketplace
    ) => {
      if (!navigateToUrl) {
        console.warn('navigateToUrl is not defined')
        return
      }

      if (!productId) {
        console.warn('productId is missing, cannot navigate')
        return
      }

      if (!resellerAuthorizationId) {
        console.warn('offerId is missing, cannot navigate')
        return
      }

      if (typeof window !== 'undefined') {
        const baseUrl = window.location.origin
        if (cloudMarketplace) {
          await dispatch(
            setCloudMarketplaceProductListing(cloudMarketplace) as never
          )
        }
        const params = new URLSearchParams({
          from: 'opportunity-action-hub',
        })
        let fullPath = `${baseUrl}/external-apps/salesforce/partners/${cloudAccountId || partnerId}/flyout/products-listing/${productId}/resale-authorizations/${resellerAuthorizationId}/details?${params.toString()}`
        if (draft) {
          fullPath = `${baseUrl}/external-apps/salesforce/partners/${cloudAccountId || partnerId}/flyout/products-listing/${productId}/resale-authorizations/${resellerAuthorizationId}/edit?${params.toString()}`
          await actions.openModal(fullPath, 'Edit Reseller Authorization')
        } else {
          await navigateToUrl(fullPath)
        }
      }
    },
    [navigateToUrl, partnerId]
  )

  const handleCreateClick = useCallback(() => {
    if (!partnerId) {
      console.error('partnerId is not available')
      return
    }
    try {
      if (typeof window !== 'undefined') {
        const baseUrl = window.location.origin
        const modalUrl = `${baseUrl}/external-apps/salesforce/partners/${partnerId}/flyout/resale-authorizations/create`

        actions.openModal(modalUrl, 'Create reseller authorization')
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        `openModal to create offer failed for partner: ${partnerId}`,
        e
      )
    }
  }, [partnerId, actions])

  if (loading) {
    return (
      <div className={classes.cardContainer}>
        <div className={classes.metaLabel}>
          Loading Reseller Authorizations...
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={classes.cardContainer}>
        <div
          className={`${classes.metaLabel} ${classes.errorText}`}
          role="alert"
        >
          {error}
        </div>
      </div>
    )
  }

  if (resellerAuthorizations.length === 0) {
    return (
      <div className={classes.emptyStateContainer}>
        <div className={classes.emptyStateText}>
          No reseller authorizations yet.
        </div>
        <ButtonV2
          styleType="link"
          onClick={handleCreateClick}
          data-testid="add-a-user-button"
          smallVariant
          icon={<FontAwesomeIcon icon={faPlus} width="14px" height="14px" />}
        >
          Create
        </ButtonV2>
      </div>
    )
  }

  return (
    <div className={classes.cardContainer}>
      {resellerAuthorizations.map((resellerAuthorization, index) => {
        const resellerAuthorizationId = resellerAuthorization.id
        const uniqueResellerAuthorizationId = `reseller-authorization-${index}-${resellerAuthorizationId}`
        const isCollapsed = collapsedResellerAuthorizations.has(
          uniqueResellerAuthorizationId
        )

        return (
          <ResellerAuthorizationCard
            key={uniqueResellerAuthorizationId}
            resellerAuthorization={resellerAuthorization}
            index={index}
            isCollapsed={isCollapsed}
            onToggleCollapse={() =>
              toggleResellerAuthorizationCollapse(uniqueResellerAuthorizationId)
            }
            onResellerAuthorizationClick={handleResellerAuthorizationClick}
          />
        )
      })}
    </div>
  )
}
